import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import NiceModal from "@ebay/nice-modal-react";
import { registerPack } from "@api/pack";
import { trackWebEvent } from "@api/web";
import Loader from "@components/Loader";
import QrCodeConfirm from "./QrCodeConfirm";
import QrPoll from "./QrPoll";
import QrComplete from "./QrComplete";

export default function QrRegister() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState("confirm");
  const [completedByUser, setCompletedByUser] = useState(false);
  const [resolveData, setResolveData] = useState(null);

  const queryClient = useQueryClient();

  const eventTrackedRef = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["pack-register", code],
    queryFn: () => registerPack(code),
    retry: false,
    enabled: !!code,
  });

  useEffect(() => {
    if (eventTrackedRef.current) return;
    if (!code || isLoading) return;
    if (!data && !error) return;

    const loginSource = sessionStorage.getItem("loginSource");
    if (!loginSource) return;

    let packRegisterStatus;
    if (error) {
      packRegisterStatus = error.message || "error";
    } else if (data?.error) {
      packRegisterStatus = data.error;
    } else if (data?.activation?.expired) {
      packRegisterStatus = "expired";
    } else if (data?.activation?.completed) {
      packRegisterStatus = "already_registered";
    } else {
      packRegisterStatus = "success";
    }

    eventTrackedRef.current = true;
    trackWebEvent(loginSource, {
      code,
      pack_register_status: packRegisterStatus,
    }).finally(() => {
      if (sessionStorage.getItem("scanMethod") === "app_scanner") {
        trackWebEvent("scanner_scan", { code });
        sessionStorage.removeItem("scanMethod");
      }
    });
  }, [code, isLoading, error, data]);

  useEffect(() => {
    if (!code) {
      navigate("/qr-scanner-landing", { replace: true });
      NiceModal.show("qr-error-modal", { type: "error_broken_link" });
    } else if (data?.error) {
      navigate("/qr-scanner-landing", { replace: true });
      NiceModal.show("qr-error-modal", { type: data.error });
    } else if (error) {
      navigate("/qr-scanner-landing", { replace: true });
      NiceModal.show("qr-error-modal");
    } else if (data?.activation?.completed && !completedByUser) {
      navigate("/qr-scanner-landing", { replace: true });
      NiceModal.show("qr-error-modal", { type: "error_pack_already_registered" });
    }
  }, [code, error, data?.error, data?.activation?.completed, completedByUser]);

  if (isLoading) {
    return <Loader show={true} blackBg={true} />;
  }

  if (error || data?.error) {
    return null;
  }

  if (data?.activation?.completed && !completedByUser) {
    return null;
  }

  if (data?.activation?.completed && completedByUser) {
    return <QrComplete activation={data?.activation} resolveData={resolveData} />;
  }

  // TODO: dedicated expired state not in use
  if (data?.activation?.expired) {
    return null;
  }

  if (step === "confirm") {
    return (
      <QrCodeConfirm
        code={code}
        onNext={() => {
          trackWebEvent("poll_start", { code });
          setStep("poll");
        }}
      />
    );
  }

  return (
    <QrPoll
      packUuid={data.uuid}
      activation={data.activation}
      onComplete={(resolveData) => {
          trackWebEvent("poll_finish", {
            code,
            bonusPrize: !!resolveData?.bonusPrize,
          });
          setResolveData(resolveData);
          setCompletedByUser(true);
          refetch();
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["user-details"] });
            queryClient.invalidateQueries({ queryKey: ["message-list"] });
            queryClient.invalidateQueries({ queryKey: ["pack-stats"] });
          }, 300);
        }}
    />
  );
}
